import { apiGet, apiPost } from '../../../adapters/api-client/api';

export type EnrollmentState =
  | 'PendingPayment'
  /** D-P07-03: card authorized, not charged; the teacher has until `acceptBy`. */
  | 'PendingTeacherAcceptance'
  | 'Active'
  | 'PaymentFailed'
  /** The teacher declined; the authorization was released, nothing charged. */
  | 'TeacherDeclined'
  /** Nobody answered in time; the authorization was released, nothing charged. */
  | 'AcceptanceExpired'
  | 'Cancelled'
  | 'Completed';

/** D-P07-04 — the five governed reasons a teacher may decline with. */
export type TeacherDeclineReason =
  | 'SCHEDULE_CONFLICT'
  | 'CLASS_NOT_VIABLE'
  | 'LEARNER_NOT_A_FIT'
  | 'CAPACITY_CHANGED'
  | 'TEACHER_UNAVAILABLE';

export type MilestoneType = 'session' | 'final_project';
export type MilestoneStatus = 'pending' | 'in_progress' | 'completed' | 'released' | 'disputed';

export interface CycleMilestone {
  sequence: number;
  type: MilestoneType;
  amount: number;
}

export interface CycleSummary {
  courseId: string;
  childId: string;
  total: number;
  sessionCount: number;
  finalProjectIncluded: boolean;
  escrow: {
    bookingFee: number;
    perSession: number;
    holdback: number;
  };
  milestones: CycleMilestone[];
}

export interface Enrollment {
  enrollmentId: string;
  /** The learning promise bought; names the closing milestone (DEC-S3-T10-02). */
  learningProfile?: string | null;
  parentId: string;
  childId: string;
  courseId: string;
  teacherId: string;
  state: EnrollmentState;
  total: number;
  /** What every amount on this enrollment is denominated in. */
  currency: string;
  bookingFeeAmount: number;
  perSessionAmount: number;
  holdbackAmount: number;
  authorizationId?: string;
  paymentFailureReason?: string;
  /** DEC-S2-S04-01: absent on an enrollment bought before versions or offers existed. */
  courseVersionId?: string;
  offerId?: string;
  /** When the card was authorized (D-P07-03). */
  authorizedAt?: string;
  /** The teacher's deadline to answer. */
  acceptBy?: string;
  declineReason?: TeacherDeclineReason;
  /** When the charge actually happened — only after the teacher accepted. */
  capturedAt?: string;
  /** When an uncaptured authorization was released (decline, expiry, cancel). */
  authorizationReleasedAt?: string;
  bookingFeeReleased: boolean;
  holdbackReleased: boolean;
  holdbackReleasedAt?: string;
  finalMilestoneCompletedAt?: string;
  refundedAmount: number;
  cancelledAt?: string;
  cancellationReason?: string;
  enrollmentDate: string;
  activatedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Milestone {
  milestoneId: string;
  enrollmentId: string;
  sequence: number;
  type: MilestoneType;
  amount: number;
  status: MilestoneStatus;
  completedAt?: string;
  releasedAt?: string;
  releaseId?: string;
  disputeId?: string;
  bookingId?: string;
  sessionState?: string;
  recordingStatus?: string;
  createdAt: string;
  updatedAt: string;
}

export interface EnrollmentDetail {
  enrollment: Enrollment;
  milestones: Milestone[];
}

export function previewCycle(courseId: string, childId: string): Promise<{ cycle: CycleSummary }> {
  const params = new URLSearchParams({ courseId, childId });
  return apiGet(`/api/v1/enrollments/preview?${params.toString()}`);
}

export interface StartCheckoutResponse {
  enrollmentId: string;
  checkoutUrl: string;
}

/**
 * The recording notice the guardian accepts at enrollment (MVP-01), with its
 * version. The server owns the words; the screen renders them as sent.
 */
export interface RecordingConsentNotice {
  version: string;
  /** Every class is recorded. Accepting is required to enroll. */
  capture: string;
  /** The separate, optional permission to watch recordings back. */
  playback: string;
}

export function getRecordingConsent(): Promise<RecordingConsentNotice> {
  return apiGet('/api/v1/enrollments/recording-consent');
}

export interface StartCheckoutInput {
  courseId: string;
  childId: string;
  /** DEC-S2-S04-01: the offer read. The server refuses OFFER_CHANGED if it has moved on. */
  offerId: string;
  /** The total the parent was shown; the server refuses TERMS_CHANGED if it moved. */
  expectedTotal?: number;
  /** Required true: the server refuses RECORDING_CONSENT_REQUIRED otherwise. */
  recordingCaptureConsent: boolean;
  /** Optional; not granted unless the guardian ticks it. */
  recordingPlaybackConsent: boolean;
  /** The notice version shown; a stale one is refused RECORDING_CONSENT_CHANGED. */
  recordingConsentVersion: string;
  /**
   * DEC-S2-S06-06 — the learner's request this checkout answers, on the version
   * the Parent was shown. The server takes learner and course from the request.
   */
  learnerRequest?: { requestId: string; expectedVersion: number };
}

/** Create (or reuse) a pending enrollment and get a hosted Stripe Checkout URL. */
export function startEnrollmentCheckout(input: StartCheckoutInput): Promise<StartCheckoutResponse> {
  const { learnerRequest, courseId, childId, offerId, ...terms } = input;
  if (learnerRequest) {
    // The server takes the offer from the request, and refuses a body that names one.
    return apiPost(
      `/api/v1/parent/learner-requests/${encodeURIComponent(learnerRequest.requestId)}/checkout`,
      { ...terms, expectedVersion: learnerRequest.expectedVersion },
    );
  }
  return apiPost('/api/v1/enrollments/checkout', { courseId, childId, offerId, ...terms });
}

/** Finalize the enrollment after returning from Stripe Checkout. */
export function confirmEnrollmentCheckout(
  enrollmentId: string,
  sessionId: string,
): Promise<EnrollmentDetail> {
  return apiPost('/api/v1/enrollments/checkout/confirm', { enrollmentId, sessionId });
}

/**
 * An enrollment plus the names the aggregate only stores as IDs.
 *
 * Every field is optional because the server enriches best-effort: a course or
 * teacher that can no longer be resolved leaves its name undefined rather than
 * dropping the enrollment from the list.
 */
export interface EnrichedEnrollment extends Enrollment {
  courseTitle?: string;
  courseSubject?: string;
  childName?: string;
  teacherName?: string;
}

export function getMyEnrollments(): Promise<{ enrollments: EnrichedEnrollment[] }> {
  return apiGet('/api/v1/enrollments/my-enrollments');
}

export function getEnrollmentDetail(enrollmentId: string): Promise<EnrollmentDetail> {
  return apiGet(`/api/v1/enrollments/${enrollmentId}`);
}

/** Why money went back to the family; labelled by the screen. */
export type ReceiptRefundReason =
  | 'CANCELLATION'
  | 'DISPUTE'
  | 'TEACHER_NO_SHOW'
  | 'SUPPORT'
  | 'OTHER';

/**
 * PRT-07-FR-12 — a parent's receipt for one enrollment, derived by the server
 * from the payment reference and the REFUND# records. No card data exists in
 * it to show (BR-16).
 */
export interface EnrollmentReceipt {
  enrollmentId: string;
  courseId: string;
  courseTitle?: string;
  childName?: string;
  state: EnrollmentState;
  currency: string;
  charge: { amount: number; paymentReference: string; chargedAt: string | null } | null;
  refunds: Array<{
    amount: number;
    reason: ReceiptRefundReason;
    refundedAt: string;
    reference?: string;
    /** TEACHER_NO_SHOW only: the session that was not given (MC-03). */
    sessionSequence?: number;
  }>;
  totals: { charged: number; refunded: number; net: number };
  unitemisedRefunded: number;
}

/** Owner-only: the server refuses anyone but the parent of record. */
export function getEnrollmentReceipt(enrollmentId: string): Promise<EnrollmentReceipt> {
  return apiGet(`/api/v1/enrollments/${enrollmentId}/receipt`);
}

/** One request waiting for the signed-in teacher (TCH-13-FR-06). */
export interface PendingEnrollmentRequest {
  enrollmentId: string;
  courseId: string;
  courseTitle?: string;
  childFirstName?: string;
  total: number;
  currency: string;
  authorizedAt?: string;
  acceptBy?: string;
  captureFailureReason?: string;
}

export interface PendingEnrollmentRequests {
  requests: PendingEnrollmentRequest[];
  /** Server time, so the countdown does not trust the device clock. */
  now: string;
  declineReasons: TeacherDeclineReason[];
}

export function getTeacherEnrollmentRequests(): Promise<PendingEnrollmentRequests> {
  return apiGet('/api/v1/enrollments/teacher/requests');
}

/** Accept: the server captures the authorized payment, then activates. */
export function acceptEnrollmentRequest(enrollmentId: string): Promise<EnrollmentDetail> {
  return apiPost(`/api/v1/enrollments/${enrollmentId}/accept`, {});
}

/** Decline with one of the governed reasons; the authorization is released. */
export function declineEnrollmentRequest(
  enrollmentId: string,
  reason: TeacherDeclineReason,
): Promise<{ enrollment: Enrollment }> {
  return apiPost(`/api/v1/enrollments/${enrollmentId}/decline`, { reason });
}
